<template>
  <BaseModal
    :is-open="isOpen"
    title="Camera & Microphone Permissions"
    max-width="600px"
    @close="handleClose"
  >
    <div class="permission-help-container">
      <!-- Permission Status -->
      <div class="permission-status" :class="statusClass">
        <div class="status-icon">
          <span class="material-symbols-outlined">{{ statusIcon }}</span>
        </div>
        <div class="status-info">
          <h3 class="status-title">{{ statusTitle }}</h3>
          <p class="status-message">{{ statusMessage }}</p>
        </div>
      </div>

      <!-- Troubleshooting Steps -->
      <div class="troubleshooting-section">
        <h4 class="section-title">
          <span class="material-symbols-outlined">build</span>
          <span>Troubleshooting Steps</span>
        </h4>

        <div class="steps-list">
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-content">
              <h5 class="step-title">Check Browser Permissions</h5>
              <p class="step-description">
                Click the lock icon in your browser's address bar and ensure camera and microphone
                are set to "Allow"
              </p>
            </div>
          </div>

          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-content">
              <h5 class="step-title">Reload the Page</h5>
              <p class="step-description">
                After changing permissions, reload the page (F5 or Cmd/Ctrl+R) to apply the changes
              </p>
            </div>
          </div>

          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-content">
              <h5 class="step-title">Check System Permissions</h5>
              <p class="step-description">
                Ensure your operating system allows your browser to access camera and microphone
              </p>
            </div>
          </div>

          <div class="step-item">
            <div class="step-number">4</div>
            <div class="step-content">
              <h5 class="step-title">Check Device Connections</h5>
              <p class="step-description">
                Verify your camera and microphone are properly connected and not in use by another
                application
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Browser-Specific Instructions -->
      <div class="browser-instructions">
        <h4 class="section-title">
          <span class="material-symbols-outlined">web</span>
          <span>Browser-Specific Instructions</span>
        </h4>

        <div class="browser-tabs">
          <button
            v-for="browser in browsers"
            :key="browser.name"
            @click="selectedBrowser = browser.name"
            class="browser-tab"
            :class="{ active: selectedBrowser === browser.name }"
          >
            {{ browser.name }}
          </button>
        </div>

        <div class="browser-content">
          <div v-for="browser in browsers" :key="browser.name">
            <div v-if="selectedBrowser === browser.name" class="browser-steps">
              <ol class="instruction-list">
                <li v-for="(step, index) in browser.steps" :key="index">
                  {{ step }}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <!-- Requirements Note -->
      <div class="requirements-note">
        <span class="material-symbols-outlined">info</span>
        <span>
          <strong>Note:</strong> Camera and microphone require HTTPS or localhost. Video chat is
          optional and does not affect gameplay.
        </span>
      </div>

      <!-- Close Button -->
      <button @click="handleClose" class="btn-close">
        <span class="material-symbols-outlined">check_circle</span>
        <span>Got It</span>
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * PermissionHelpModal Component
 *
 * Provides troubleshooting help for camera/microphone permission issues.
 * Shows browser-specific instructions and common solutions.
 *
 * Props:
 * - permission_state: Current permission state ('granted', 'denied', 'prompt')
 * - error_message: Optional error message
 *
 * Emits:
 * - close: [] - Modal closed
 */

type PermissionState = 'granted' | 'denied' | 'prompt'

interface Props {
  isOpen: boolean
  data: {
    permission_state?: PermissionState
    error_message?: string
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  close: []
}>()

const selectedBrowser = ref('Chrome')

/**
 * Permission state
 */
const permissionState = computed(() => props.data?.permission_state || 'prompt')

/**
 * Status configuration
 */
const statusClass = computed(() => {
  if (permissionState.value === 'granted') return 'success'
  if (permissionState.value === 'denied') return 'error'
  return 'warning'
})

const statusIcon = computed(() => {
  if (permissionState.value === 'granted') return 'check_circle'
  if (permissionState.value === 'denied') return 'block'
  return 'help'
})

const statusTitle = computed(() => {
  if (permissionState.value === 'granted') return 'Permissions Granted'
  if (permissionState.value === 'denied') return 'Permissions Denied'
  return 'Permissions Not Set'
})

const statusMessage = computed(() => {
  if (permissionState.value === 'granted') {
    return 'Your browser has access to camera and microphone.'
  }
  if (permissionState.value === 'denied') {
    return props.data?.error_message || 'Your browser is blocked from accessing camera and microphone.'
  }
  return 'Camera and microphone permissions have not been requested yet.'
})

/**
 * Browser instructions
 */
const browsers = [
  {
    name: 'Chrome',
    steps: [
      'Click the lock icon in the address bar',
      'Find "Camera" and "Microphone" in the permissions list',
      'Change both to "Allow"',
      'Reload the page',
    ],
  },
  {
    name: 'Firefox',
    steps: [
      'Click the lock icon in the address bar',
      'Click "Connection secure" or "Connection not secure"',
      'Click "More information"',
      'Go to the "Permissions" tab',
      'Check "Use the Camera" and "Use the Microphone"',
      'Reload the page',
    ],
  },
  {
    name: 'Safari',
    steps: [
      'Go to Safari menu → Settings',
      'Click "Websites" tab',
      'Select "Camera" and "Microphone"',
      'Find this website and change to "Allow"',
      'Reload the page',
    ],
  },
  {
    name: 'Edge',
    steps: [
      'Click the lock icon in the address bar',
      'Find "Camera" and "Microphone"',
      'Change both to "Allow"',
      'Reload the page',
    ],
  },
]

/**
 * Handle close
 */
function handleClose(): void {
  emit('close')
}
</script>

<style scoped>
.permission-help-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Permission Status */
.permission-status {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid;
}

.permission-status.success {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
}

.permission-status.error {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
}

.permission-status.warning {
  background: rgba(251, 191, 36, 0.1);
  border-color: rgba(251, 191, 36, 0.3);
}

.status-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.permission-status.success .status-icon {
  background: rgba(16, 185, 129, 0.2);
}

.permission-status.error .status-icon {
  background: rgba(239, 68, 68, 0.2);
}

.permission-status.warning .status-icon {
  background: rgba(251, 191, 36, 0.2);
}

.status-icon .material-symbols-outlined {
  font-size: 2rem;
}

.permission-status.success .status-icon .material-symbols-outlined {
  color: #10b981;
}

.permission-status.error .status-icon .material-symbols-outlined {
  color: #f87171;
}

.permission-status.warning .status-icon .material-symbols-outlined {
  color: #fbbf24;
}

.status-info {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
}

.status-title {
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
}

.permission-status.success .status-title {
  color: #10b981;
}

.permission-status.error .status-title {
  color: #f87171;
}

.permission-status.warning .status-title {
  color: #fbbf24;
}

.status-message {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  margin: 0;
}

/* Troubleshooting Section */
.troubleshooting-section,
.browser-instructions {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.section-title .material-symbols-outlined {
  font-size: 1.25rem;
  color: #e8c547;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.step-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e8c547, #d4af37);
  color: #0d1f2d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  flex-shrink: 0;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
}

.step-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.step-description {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  margin: 0;
}

/* Browser Instructions */
.browser-tabs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.browser-tab {
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
}

.browser-tab:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.25);
}

.browser-tab.active {
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.2), rgba(212, 175, 55, 0.2));
  border-color: #e8c547;
  color: #e8c547;
}

.browser-content {
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}

.instruction-list {
  margin: 0;
  padding-left: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.instruction-list li {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
}

/* Requirements Note */
.requirements-note {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 1rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  font-size: 0.875rem;
  color: rgba(147, 197, 253, 0.9);
  line-height: 1.6;
}

.requirements-note .material-symbols-outlined {
  font-size: 1.125rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* Close Button */
.btn-close {
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
}

.btn-close:hover {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-close:active {
  transform: translateY(0);
}

.btn-close .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Responsive */
@media (max-width: 640px) {
  .permission-status {
    flex-direction: column;
    text-align: center;
  }

  .status-info {
    align-items: center;
  }

  .browser-tabs {
    justify-content: center;
  }
}
</style>
